import apiClient from './index';

export type PulseConnectedDevice = {
  id: string;
  serial_number?: string | null;
  system_type?: string | null;
  apartment_id?: string | null;
  apartment_number?: string | null;
  status?: string | null;
  last_data_time?: string | null;
  is_problem?: boolean;
};

export type RadioModuleChannel = {
  id: string;
  number: number;
  name?: string | null;
  is_active: boolean;
  device?: PulseConnectedDevice | null;
  connection_id?: string | null;
  pulse_weight?: string | number | null;
  pulse_unit?: string | null;
  pulse_input_mode?: string | null;
  pulse_input_value?: string | number | null;
  baseline_pulse_count?: number | null;
  baseline_meter_value?: string | number | null;
  coefficient_source?: string | null;
  active_from?: string | null;
};

export type RadioModule = {
  id: string;
  building_id: string;
  name: string;
  equipment_type: string;
  serial_number?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  uplink_transport: string;
  channel_count: number;
  dev_eui?: string | null;
  network_address?: string | null;
  notes?: string | null;
  is_active: boolean;
  occupied_channels: number;
  channels: RadioModuleChannel[];
  created_at: string;
  path_issue_focus?: string | null;
  path_issue_label?: string | null;
  path_issue_reason?: string | null;
  devices_problem?: number;
  uplink_gateway_status?: string | null;
};

export type RadioModuleCreate = {
  building_id: string;
  name: string;
  serial_number?: string;
  manufacturer?: string;
  model?: string;
  uplink_transport: 'lorawan' | 'mqtt';
  channel_count: number;
  dev_eui?: string;
  network_address?: string;
  notes?: string;
};

export type RadioModuleBuildingTotals = {
  modules: number;
  channels: number;
  occupied: number;
  free: number;
  missing_weight: number;
  with_problems: number;
};

export type RadioModuleListResponse = {
  items: RadioModule[];
  total: number;
  page: number;
  page_size: number;
  totals: RadioModuleBuildingTotals;
};

export type RadioModuleListParams = {
  detail?: 'summary' | 'full';
  q?: string;
  occupancy?: 'all' | 'has_free' | 'full' | 'has_problems';
  uplink_transport?: string;
  is_active?: boolean;
  page?: number;
  page_size?: number;
};

export type PulseBindingCreate = {
  device_id: string;
  pulse_input_mode?: 'units_per_pulse' | 'pulses_per_unit';
  pulse_input_value?: number;
  pulse_input_unit?: 'l' | 'm3' | 'kwh' | 'gcal';
  baseline_pulse_count?: number;
  baseline_meter_value?: number;
  coefficient_source?: 'passport' | 'nameplate' | 'catalog' | 'operator' | 'calibration';
  notes?: string;
  replace_active?: boolean;
  close_reason?: string;
};

export type PulseConnection = {
  id: string;
  device_id: string;
  module_id: string;
  module_name: string;
  line_id: string;
  channel_number: number;
  pulse_weight?: string | number | null;
  pulse_unit?: string | null;
  pulse_input_mode?: string | null;
  pulse_input_value?: string | number | null;
  baseline_pulse_count?: number | null;
  baseline_meter_value?: string | number | null;
  coefficient_source?: string | null;
  active_from: string;
  active_to?: string | null;
  close_reason?: string | null;
  notes?: string | null;
};

export type PulseImportRowResult = {
  row_number: number;
  action: string;
  apartment_number?: string | null;
  device_serial?: string | null;
  registrar_key?: string | null;
  channel_number?: number | null;
  errors: string[];
  warnings: string[];
};

export type PulseImportResult = {
  ok: boolean;
  registrars_to_create: number;
  registrars_found: number;
  devices_to_create: number;
  devices_found: number;
  bindings_to_create: number;
  bindings_to_update: number;
  empty_channels_after: number;
  error_count: number;
  warning_count: number;
  rows: PulseImportRowResult[];
};

export const communicationEquipmentApi = {
  async listRadioModules(
    buildingId: string,
    detailOrParams: 'summary' | 'full' | RadioModuleListParams = 'summary',
  ): Promise<RadioModuleListResponse> {
    const params: RadioModuleListParams =
      typeof detailOrParams === 'string' ? { detail: detailOrParams } : detailOrParams || {};
    const response = await apiClient.get<RadioModuleListResponse>(
      `/communication-equipment/buildings/${buildingId}/radio-modules`,
      {
        params: {
          detail: params.detail || 'summary',
          q: params.q || undefined,
          occupancy: params.occupancy || 'all',
          uplink_transport: params.uplink_transport || undefined,
          is_active: params.is_active,
          page: params.page ?? 1,
          page_size: params.page_size ?? 20,
        },
      }
    );
    return response.data;
  },

  async getRadioModule(moduleId: string): Promise<RadioModule> {
    const response = await apiClient.get<RadioModule>(`/communication-equipment/radio-modules/${moduleId}`);
    return response.data;
  },

  async createRadioModule(payload: RadioModuleCreate): Promise<RadioModule> {
    const response = await apiClient.post<RadioModule>('/communication-equipment/radio-modules', payload);
    return response.data;
  },

  async bindDevice(moduleId: string, channelNumber: number, payload: PulseBindingCreate): Promise<PulseConnection> {
    const response = await apiClient.post<PulseConnection>(
      `/communication-equipment/radio-modules/${moduleId}/channels/${channelNumber}/bind`,
      payload
    );
    return response.data;
  },

  async closeConnection(connectionId: string, reason: string): Promise<PulseConnection> {
    const response = await apiClient.post<PulseConnection>(
      `/communication-equipment/pulse-connections/${connectionId}/close`,
      { reason }
    );
    return response.data;
  },

  async updateMetrology(connectionId: string, payload: Record<string, unknown>): Promise<PulseConnection> {
    const response = await apiClient.patch<PulseConnection>(
      `/communication-equipment/pulse-connections/${connectionId}/metrology`,
      payload
    );
    return response.data;
  },

  async listDeviceConnections(deviceId: string): Promise<PulseConnection[]> {
    const response = await apiClient.get<PulseConnection[]>(
      `/communication-equipment/devices/${deviceId}/pulse-connections`
    );
    return response.data;
  },

  async downloadImportTemplate(buildingId: string): Promise<Blob> {
    const response = await apiClient.get(
      `/communication-equipment/buildings/${buildingId}/pulse-import/template`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  async exportChannelMap(buildingId: string): Promise<Blob> {
    const response = await apiClient.get(
      `/communication-equipment/buildings/${buildingId}/pulse-export`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  async previewImport(buildingId: string, file: File): Promise<PulseImportResult> {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.post<PulseImportResult>(
      `/communication-equipment/buildings/${buildingId}/pulse-import/preview`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  async runImport(buildingId: string, file: File): Promise<PulseImportResult> {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.post<PulseImportResult>(
      `/communication-equipment/buildings/${buildingId}/pulse-import`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
};
